import { useMemo, useState } from 'react';
import { NODE_TYPE_DESCRIPTIONS, NODE_TYPE_LABELS } from '../../lib/nodeConfig';
import { NODE_SHAPES, type NodeType } from '../../types/project';
import {
  clampGridSize,
  effectiveGridSize,
  GRID_SIZE_PRESETS,
} from '../../lib/grid';
import { unitShort } from '../../lib/calibration';
import {
  clampNodeTypeRadius,
  MAX_NODE_TYPE_RADIUS,
  MIN_NODE_TYPE_RADIUS,
  NODE_SHAPE_LABELS,
  patchNodeTypeAppearance,
  resolveNodeTypeAppearance,
} from '../../lib/nodeVisualGeometry';
import { useEditorStore } from '../../store/editorStore';
import { useEditorLayoutStore } from '../../store/editorLayoutStore';
import { useSimulationStore } from '../../store/simulationStore';
import type { EditorPanelId } from '../../types/editorLayout';
import LayoutPanel from './LayoutPanel';
import { ElementsList, PropertiesContent } from './PropertiesPanel';
import { NodeShapePreview } from './NodeBodyShape';

// ------------------------------------------------------------
// Dock panel: node palette + per-type appearance
// ------------------------------------------------------------

export function NodePalettePanelContent() {
  const addNode = useEditorStore((s) => s.addNode);
  const permission = useEditorStore((s) => s.permission);
  const document = useEditorStore((s) => s.document);
  const updateSettings = useEditorStore((s) => s.updateSettings);
  const readOnly = permission !== 'edit';
  const [expandedType, setExpandedType] = useState<NodeType | null>(null);

  const nodeTypes = useMemo(
    () => Object.keys(NODE_TYPE_LABELS) as NodeType[],
    [],
  );

  const updateTypeAppearance = (
    type: NodeType,
    patch: { color?: string; radius?: number; shape?: (typeof NODE_SHAPES)[number] },
  ) => {
    if (readOnly) return;
    updateSettings(patchNodeTypeAppearance(document?.settings, type, patch));
  };

  return (
    <div className="p-2">
      <p className="px-2 pb-2 text-[11px] text-text-subtle">
        {readOnly
          ? 'View-only mode'
          : 'Click + to place · expand a type to edit color, size, and shape for all nodes of that type'}
      </p>
      <ul className="space-y-1">
        {nodeTypes.map((type) => {
          const appearance = resolveNodeTypeAppearance(type, document?.settings);
          const expanded = expandedType === type;

          return (
            <li
              key={type}
              className="overflow-hidden rounded-lg border border-transparent transition hover:border-border/60"
            >
              <div className="flex items-center gap-1.5 px-1.5 py-1">
                <button
                  type="button"
                  onClick={() => setExpandedType(expanded ? null : type)}
                  className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1.5 text-left text-sm text-text-muted transition hover:bg-surface-overlay hover:text-text"
                  title={NODE_TYPE_DESCRIPTIONS[type]}
                >
                  <svg
                    width={18}
                    height={18}
                    viewBox="0 0 18 18"
                    className="shrink-0"
                    aria-hidden
                  >
                    <NodeShapePreview
                      shape={appearance.shape}
                      color={appearance.color}
                      size={18}
                    />
                  </svg>
                  <span className="truncate">{NODE_TYPE_LABELS[type]}</span>
                  <span className="ml-auto text-[10px] text-text-subtle">
                    {expanded ? '▾' : '▸'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => addNode(type)}
                  disabled={readOnly}
                  className="shrink-0 rounded-md border border-border bg-surface-overlay px-2 py-1 text-xs text-text-muted transition hover:bg-accent/15 hover:text-text disabled:cursor-not-allowed disabled:opacity-40"
                  title={`Place ${NODE_TYPE_LABELS[type]}`}
                >
                  +
                </button>
              </div>

              {expanded && (
                <div className="space-y-3 border-t border-border/60 px-3 pb-3 pt-2">
                  <div>
                    <label className="mb-1 block text-[10px] uppercase tracking-wide text-text-subtle">
                      Color
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={appearance.color}
                        disabled={readOnly}
                        onChange={(e) =>
                          updateTypeAppearance(type, { color: e.target.value })
                        }
                        className="h-8 w-10 cursor-pointer rounded border border-border bg-transparent p-0.5 disabled:cursor-not-allowed disabled:opacity-40"
                      />
                      <input
                        type="text"
                        value={appearance.color}
                        disabled={readOnly}
                        onChange={(e) =>
                          updateTypeAppearance(type, { color: e.target.value })
                        }
                        className="input-field min-w-0 flex-1 font-mono text-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-text-subtle">
                      <span>Size</span>
                      <span className="normal-case text-text-muted">
                        {appearance.radius}px
                      </span>
                    </label>
                    <input
                      type="range"
                      min={MIN_NODE_TYPE_RADIUS}
                      max={MAX_NODE_TYPE_RADIUS}
                      step={1}
                      value={appearance.radius}
                      disabled={readOnly}
                      onChange={(e) =>
                        updateTypeAppearance(type, {
                          radius: clampNodeTypeRadius(Number(e.target.value)),
                        })
                      }
                      className="w-full accent-accent"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-[10px] uppercase tracking-wide text-text-subtle">
                      Shape
                    </label>
                    <div className="grid grid-cols-4 gap-1">
                      {NODE_SHAPES.map((shape) => {
                        const active = appearance.shape === shape;
                        return (
                          <button
                            key={shape}
                            type="button"
                            disabled={readOnly}
                            onClick={() => updateTypeAppearance(type, { shape })}
                            title={NODE_SHAPE_LABELS[shape]}
                            className={`flex flex-col items-center gap-0.5 rounded-md border px-1 py-1.5 text-[9px] transition disabled:cursor-not-allowed disabled:opacity-40 ${
                              active
                                ? 'border-accent/60 bg-accent/15 text-text'
                                : 'border-border/60 bg-surface/40 text-text-subtle hover:border-border hover:text-text'
                            }`}
                          >
                            <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden>
                              <NodeShapePreview
                                shape={shape}
                                color={appearance.color}
                                size={20}
                              />
                            </svg>
                            <span className="truncate leading-none">
                              {NODE_SHAPE_LABELS[shape]}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function CanvasSettingsPanelContent() {
  const permission = useEditorStore((s) => s.permission);
  const readOnly = permission !== 'edit';
  const snapToGrid = useEditorStore((s) => s.snapToGrid);
  const showGrid = useEditorStore((s) => s.showGrid);
  const toggleSnapToGrid = useEditorStore((s) => s.toggleSnapToGrid);
  const toggleShowGrid = useEditorStore((s) => s.toggleShowGrid);
  const document = useEditorStore((s) => s.document);
  const updateSettings = useEditorStore((s) => s.updateSettings);

  const gridSize = effectiveGridSize(document?.settings);
  const pixelsPerMeter = document?.settings?.pixels_per_meter ?? 0;
  const unit = document?.settings?.unit_system ?? 'metric_m';
  const calibratedCell =
    pixelsPerMeter > 0 ? gridSize / pixelsPerMeter : null;

  const handleGridSizeChange = (value: number) => {
    if (readOnly) return;
    updateSettings({ grid_size: clampGridSize(value) });
  };

  return (
    <div className="space-y-2 p-4">
      <label className="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm text-text-muted hover:bg-surface-overlay">
        <span>Snap to grid</span>
        <input
          type="checkbox"
          checked={snapToGrid}
          onChange={toggleSnapToGrid}
          className="h-4 w-4 rounded border-border accent-accent"
        />
      </label>
      <label className="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm text-text-muted hover:bg-surface-overlay">
        <span>Show grid</span>
        <input
          type="checkbox"
          checked={showGrid}
          onChange={toggleShowGrid}
          className="h-4 w-4 rounded border-border accent-accent"
        />
      </label>

      <div className="space-y-2 px-2">
        <label className="block text-xs text-text-subtle">Grid spacing (px)</label>
        <div className="flex flex-wrap gap-1">
          {GRID_SIZE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              disabled={readOnly}
              onClick={() => handleGridSizeChange(preset)}
              className={`rounded-md px-2 py-1 text-xs transition ${
                gridSize === preset
                  ? 'badge-accent-on-dark'
                  : 'bg-surface-overlay text-text-muted hover:text-text'
              } disabled:cursor-not-allowed disabled:opacity-40`}
            >
              {preset}
            </button>
          ))}
        </div>
        <input
          type="number"
          min={5}
          max={500}
          step={1}
          value={gridSize}
          disabled={readOnly}
          onChange={(e) =>
            handleGridSizeChange(parseInt(e.target.value, 10) || gridSize)
          }
          className="input-field w-full py-1.5 text-xs"
        />
        {calibratedCell != null && (
          <p className="text-[10px] leading-relaxed text-text-subtle">
            ≈ {calibratedCell.toFixed(2)} {unitShort(unit)} per cell
          </p>
        )}
      </div>
    </div>
  );
}

export function RoutesPanelContent() {
  const permission = useEditorStore((s) => s.permission);
  const readOnly = permission !== 'edit';
  const routeSourceId = useEditorStore((s) => s.routeSourceId);
  const selectedNodeIds = useEditorStore((s) => s.selectedNodeIds);
  const selectedNodeId = selectedNodeIds.length === 1 ? selectedNodeIds[0] : null;
  const setRouteSource = useEditorStore((s) => s.setRouteSource);
  const setBottomPanelOpen = useSimulationStore((s) => s.setBottomPanelOpen);
  const setBottomPanelTab = useSimulationStore((s) => s.setBottomPanelTab);
  const setLayoutBottomOpen = useEditorLayoutStore((s) => s.setBottomOpen);
  const setLayoutBottomTab = useEditorLayoutStore((s) => s.setBottomTab);

  const handleStartRoute = () => {
    if (selectedNodeId) setRouteSource(selectedNodeId);
  };

  const openDiagnostics = () => {
    setLayoutBottomOpen(true);
    setLayoutBottomTab('diagnostics');
    setBottomPanelOpen(true);
    setBottomPanelTab('diagnostics');
  };

  return (
    <div className="space-y-2 p-4">
      <button
        type="button"
        onClick={handleStartRoute}
        disabled={!selectedNodeId || readOnly}
        className="btn-secondary w-full py-2 text-xs disabled:opacity-40"
      >
        {routeSourceId ? 'Drawing…' : 'Connect nodes'}
      </button>
      {routeSourceId && (
        <button
          type="button"
          onClick={() => setRouteSource(null)}
          className="btn-ghost w-full py-1.5 text-xs"
        >
          Cancel route
        </button>
      )}
      <button
        type="button"
        onClick={openDiagnostics}
        className="btn-ghost w-full py-1.5 text-xs"
      >
        Project diagnostics…
      </button>
      <p className="text-[10px] leading-relaxed text-text-subtle">
        Select a node, click Connect, then click the destination.
      </p>
      <p className="text-[10px] text-text-subtle">
        <kbd className="rounded bg-surface-overlay px-1 py-0.5">Del</kbd> removes
        selection ·{' '}
        <kbd className="rounded bg-surface-overlay px-1 py-0.5">Esc</kbd> cancels
        route / calibration
      </p>
    </div>
  );
}

export function ElementsPanelContent() {
  const document = useEditorStore((s) => s.document);
  const nodes = document?.nodes ?? [];
  return (
    <>
      <div className="border-b border-border px-2.5 py-1.5">
        <p className="text-[10px] text-text-subtle">
          {nodes.length} node{nodes.length !== 1 ? 's' : ''} ·{' '}
          {document?.routes.length ?? 0} route
          {(document?.routes.length ?? 0) !== 1 ? 's' : ''}
        </p>
      </div>
      <ElementsList />
    </>
  );
}

export function PropertiesInspectorPanelContent() {
  return <PropertiesContent />;
}

export function LayoutPanelContent() {
  const permission = useEditorStore((s) => s.permission);
  const readOnly = permission !== 'edit';
  return <LayoutPanel readOnly={readOnly} embedded />;
}

const PANEL_CONTENT: Record<EditorPanelId, () => JSX.Element> = {
  'node-palette': NodePalettePanelContent,
  'canvas-settings': CanvasSettingsPanelContent,
  routes: RoutesPanelContent,
  layout: LayoutPanelContent,
  elements: ElementsPanelContent,
  properties: PropertiesInspectorPanelContent,
};

export function renderEditorPanelContent(panelId: EditorPanelId) {
  const Component = PANEL_CONTENT[panelId];
  return <Component />;
}
